<template>
    <Card class="settings-export-preference">
        <Heading
            :labelTitle="$t.__('export.settings.title')"
            variant="h2"
            :prefixColoredIcon="{
                icon: 'database-smile',
                color: CompassEnums.ColoredIconColorType.BRAND,
            }"
        />
        <div class="settings-export-preference__form">
            <CompassFormItem
                :label="$t.__('export.settings.separator')"
                :orientation="CompassEnums.FormItemOrientation.HORIZONTAL"
            >
                <CompassSelect v-model="settings.separator">
                    <CompassDropdownOption
                        v-for="option in separatorOptions"
                        v-bind="option"
                        :key="option.value"
                    />
                </CompassSelect>
            </CompassFormItem>

            <CompassFormItem
                :label="$t.__('export.settings.textIdentifier')"
                :orientation="CompassEnums.FormItemOrientation.HORIZONTAL"
            >
                <CompassSelect v-model="settings.wrapper">
                    <CompassDropdownOption
                        v-for="option in wrapperOptions"
                        v-bind="option"
                        :key="option.value"
                    />
                </CompassSelect>
            </CompassFormItem>

            <CompassFormItem
                :label="$t.__('export.settings.textFieldsFormat')"
                :orientation="CompassEnums.FormItemOrientation.HORIZONTAL"
            >
                <CompassSelect v-model="settings.textFormat">
                    <CompassDropdownOption
                        v-for="option in textFormatsOptions"
                        v-bind="option"
                        :key="option.value"
                    />
                </CompassSelect>
            </CompassFormItem>

            <p class="settings-export-preference__description typographies-paragraph-small-regular">
                {{ $t.__('export.settings.textFieldsDescription') }}
            </p>

            <CompassButton
                class="settings-export-preference__save-button"
                :label="$t.__('common.actions.save')"
                :disabled="!settingUpdated"
                :loading="loading"
                @click="handleSave"
            />
        </div>
    </Card>
</template>

<script setup lang="ts">
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import { savePreferences } from 'api/settings/export/exportPreferenceService';
import { ref, watch, inject } from 'vue';
import type { Translator, PushToast } from 'js/shims-vue';

const $pushToast = inject('$pushToast') as PushToast;
const $t = inject('$t') as Translator;

const props = defineProps({
    settings: {
        type: Object,
        required: true,
    },
});

const loading = ref(false);
const settingUpdated = ref(false);
const settings = ref(props.settings);

watch(
    settings,
    () => {
        settingUpdated.value = true;
    },
    { deep: true },
);

const separatorOptions = [
    { label: $t.__('export.settings.separator.semicolon'), value: 'semicolon' },
    { label: $t.__('export.settings.separator.tab'), value: 'tab' },
    { label: $t.__('export.settings.separator.comma'), value: 'comma' },
];
const wrapperOptions = [
    { label: $t.__('export.settings.wrapper.quote'), value: 'quote' },
    { label: $t.__('export.settings.wrapper.simplequote'), value: 'simplequote' },
    { label: $t.__('export.settings.wrapper.none'), value: 'none' },
];
const textFormatsOptions = [
    { label: $t.__('export.settings.textFormat.with'), value: 'with' },
    { label: $t.__('export.settings.textFormat.without'), value: 'without' },
];

function handleSave() {
    loading.value = true;
    savePreferences(settings.value)
        .then(() => {
            settingUpdated.value = false;
            $pushToast({
                type: CompassEnums.ToastType.SUCCESS,
                icon: true,
                text: $t.__('export.settings.success_message'),
                close: true,
            });
        })
        .catch(() => {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                text: $t.__('common.labels.toast.error'),
                close: true,
            });
        })
        .finally(() => {
            loading.value = false;
        });
}
</script>

<style scoped lang="scss">
.settings-export-preference {
    max-width: 50%;

    @media screen and (max-width: 1199px) {
        max-width: inherit;
    }

    &__form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);
    }

    &__description {
        margin: 0;
    }

    &__save-button {
        display: block;
        margin-left: auto;
    }
}
</style>
